'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { createClient } from '@/lib/supabase/client';

export interface TeamMember {
  name: string;
  role: string;
  photo: string; // URL or base64
}

export interface GalleryPhoto {
  id: string;
  src: string;
  alt: string;
  caption?: string;
}

export interface EventSettings {
  eventName: string;
  venue: string;
  date: string;
  time: string;
  organizers: string[];
  schools: string[];
  trainingDurationMinutes: number;
  teamMembers: TeamMember[];
  galleryPhotos: GalleryPhoto[];
}

const DEFAULT_SETTINGS: EventSettings = {
  eventName: 'National BLS Day 2026',
  venue: 'PSG B Block Auditorium, PSG IMSR, Coimbatore',
  date: 'Friday, 24 July 2026',
  time: '9:00 AM – 4:00 PM',
  organizers: [
    'Department of Pediatrics, PSG Institute of Medical Sciences & Research (PSG IMSR)',
    'PSG Simulation Laboratory',
    'Indian Academy of Pediatrics (IAP), Coimbatore Chapter',
  ],
  schools: [
    'PSG IMSR',
    'PSG College of Arts & Science',
    'PSG College of Technology',
    'Kovai Medical Center',
    'KMCH College of Nursing',
    'Sri Ramakrishna Hospital',
    'Coimbatore Medical College',
    'GRH Coimbatore',
  ],
  trainingDurationMinutes: 420,
  teamMembers: [],
  galleryPhotos: [],
};

const STORAGE_KEY = 'smartbls_event_settings';

interface EventSettingsContextValue {
  settings: EventSettings;
  isLoading: boolean;
  isSaving: boolean;
  saveError: string | null;
  updateSettings: (updated: Partial<EventSettings>) => Promise<boolean>;
  resetSettings: () => void;
}

const EventSettingsContext = createContext<EventSettingsContextValue | null>(null);

export function EventSettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<EventSettings>(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Load settings: first from localStorage (fast), then sync from Supabase (authoritative)
  useEffect(() => {
    // Step 1: Load from localStorage immediately for fast render
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        setSettings({ ...DEFAULT_SETTINGS, ...JSON.parse(stored) });
      }
    } catch {
      // ignore parse errors
    }

    // Step 2: Fetch from Supabase (authoritative source — always overrides localStorage)
    const supabase = createClient();
    supabase
      .from('event_settings')
      .select('settings')
      .eq('id', 1)
      .single()
      .then(({ data, error }) => {
        if (!error && data?.settings) {
          const remote = data.settings as Partial<EventSettings>;
          // Always apply Supabase data — it is the authoritative source
          // Ensure teamMembers is always an array even if missing from stored data
          const merged: EventSettings = {
            ...DEFAULT_SETTINGS,
            ...remote,
            teamMembers: Array.isArray(remote.teamMembers) ? remote.teamMembers : [],
            galleryPhotos: Array.isArray(remote.galleryPhotos) ? remote.galleryPhotos : [],
          };
          setSettings(merged);
          // Sync back to localStorage so next load is fast
          try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
          } catch {
            // ignore
          }
        }
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  }, []);

  const updateSettings = async (updated: Partial<EventSettings>): Promise<boolean> => {
    // Compute next state outside setSettings to avoid calling Supabase inside React updater
    const next: EventSettings = { ...settings, ...updated };

    // Optimistically update local state
    setSettings(next);
    setSaveError(null);

    // Save to localStorage for fast access
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // ignore storage errors
    }

    // Save to Supabase so all devices see the update
    setIsSaving(true);
    const supabase = createClient();
    const { error } = await supabase
      .from('event_settings')
      .upsert({ id: 1, settings: next, updated_at: new Date().toISOString() });

    setIsSaving(false);

    if (error) {
      console.error('Failed to save event settings to Supabase:', error.message);
      setSaveError(error.message);
      return false;
    }

    return true;
  };

  const resetSettings = () => {
    setSettings(DEFAULT_SETTINGS);
    setSaveError(null);

    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }

    // Reset in Supabase too
    const supabase = createClient();
    supabase
      .from('event_settings')
      .upsert({ id: 1, settings: DEFAULT_SETTINGS, updated_at: new Date().toISOString() })
      .then(({ error }) => {
        if (error) {
          console.error('Failed to reset event settings in Supabase:', error.message);
        }
      });
  };

  return (
    <EventSettingsContext.Provider value={{ settings, isLoading, isSaving, saveError, updateSettings, resetSettings }}>
      {children}
    </EventSettingsContext.Provider>
  );
}

export function useEventSettings(): EventSettingsContextValue {
  const ctx = useContext(EventSettingsContext);
  if (!ctx) throw new Error('useEventSettings must be used within EventSettingsProvider');
  return ctx;
}
